import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Phone, Menu, X, LogIn, LogOut, LayoutDashboard } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ASSETS, PHONE } from "@/lib/site";
import { useAuth } from "@/lib/auth";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const links = [
  { id: "about", label: "About BFR" },
  { id: "manifesto", label: "Philosophy" },
  { id: "remedies", label: "38 Remedies" },
  { id: "quiz", label: "Find Yours" },
  { id: "audience", label: "For\nyou\u2009?" },
  { id: "practitioner", label: "About Me" },
  { id: "remedy", label: "Remedy" },
  { id: "shop", label: "Ready Mixes" },
  { id: "testimonials", label: "Stories" },
  { id: "brochure", label: "Brochure" },
];

export default function Navbar({ onBookNow }) {
  const [open, setOpen] = useState(false);
  const { user, login, logout } = useAuth();

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const initials = user ? (user.name || user.email || "?").trim()[0].toUpperCase() : "";

  return (
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-40 backdrop-blur-md bg-[#FDFBF7]/80 border-b border-[#EAE4DA]"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="nav-logo">
          <img
            src={ASSETS.logo}
            alt="Bloom & Heal"
            className="h-10 w-10 rounded-full object-cover ring-1 ring-[#D5B876]/40"
          />
          <div className="leading-none">
            <div className="font-serif text-xl sm:text-2xl text-[#0B302F] tracking-tight">Bloom &amp; Heal</div>
            <div className="text-[10px] tracking-[0.28em] uppercase text-[#2A5C5A] mt-0.5">Bach Flower Remedies</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-7 flex-1 justify-center">
          {links.map((l) => (
            <button
              key={l.id}
              onClick={() => go(l.id)}
              data-testid={`nav-link-${l.id}`}
              className="text-sm font-sans text-[#0B302F]/80 hover:text-[#0B302F] transition-colors tracking-wide whitespace-pre-line leading-tight text-center"
            >
              {l.label}
            </button>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3 shrink-0">
          <a
            href={`tel:${PHONE}`}
            data-testid="nav-phone"
            className="hidden xl:flex items-center gap-2 text-sm text-[#0B302F]/80 hover:text-[#0B302F]"
          >
            <Phone className="h-4 w-4" strokeWidth={1.5} /> {PHONE}
          </a>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="nav-user-avatar"
                  className="h-9 w-9 rounded-full bg-[#0B302F] text-[#FDFBF7] flex items-center justify-center font-serif ring-1 ring-[#D5B876]/40 overflow-hidden"
                >
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="h-full w-full object-cover" />
                  ) : (
                    <span>{initials}</span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-white border-[#EAE4DA]">
                <DropdownMenuLabel className="font-sans">
                  <div className="text-xs text-[#2A5C5A] tracking-[0.18em] uppercase">Signed in</div>
                  <div className="mt-1 truncate text-[#0B302F]">{user.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild data-testid="nav-menu-dashboard">
                  <Link to="/dashboard" className="cursor-pointer">
                    <LayoutDashboard className="h-4 w-4 mr-2" strokeWidth={1.5} /> My bookings
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={logout}
                  data-testid="nav-menu-logout"
                  className="cursor-pointer text-[#0B302F]"
                >
                  <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button
              onClick={login}
              data-testid="nav-signin"
              variant="ghost"
              className="text-xs tracking-[0.18em] uppercase text-[#0B302F]/80 hover:text-[#0B302F] hover:bg-transparent"
            >
              <LogIn className="h-4 w-4 mr-2" strokeWidth={1.5} /> Sign in
            </Button>
          )}

          <Button
            onClick={onBookNow}
            data-testid="nav-book-now"
            className="rounded-full bg-[#0B302F] text-[#FDFBF7] hover:bg-[#2A5C5A] text-xs tracking-[0.18em] uppercase px-5"
          >
            Book Now
          </Button>
        </div>

        <button
          onClick={() => setOpen((o) => !o)}
          className="md:hidden p-2 rounded-md text-[#0B302F]"
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[#EAE4DA] bg-[#FDFBF7]" data-testid="nav-mobile-menu">
          <div className="px-5 py-4 flex flex-col gap-3">
            {links.map((l) => (
              <button
                key={l.id}
                onClick={() => go(l.id)}
                data-testid={`nav-mobile-link-${l.id}`}
                className="text-left text-[#0B302F] py-1 font-sans"
              >
                {l.label}
              </button>
            ))}
            <a href={`tel:${PHONE}`} className="flex items-center gap-2 text-[#0B302F] py-1">
              <Phone className="h-4 w-4" strokeWidth={1.5} /> {PHONE}
            </a>
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-2 text-[#0B302F] py-1"
                  data-testid="nav-mobile-dashboard"
                >
                  <LayoutDashboard className="h-4 w-4" strokeWidth={1.5} /> My bookings
                </Link>
                <button
                  onClick={() => { setOpen(false); logout(); }}
                  className="flex items-center gap-2 text-[#0B302F] py-1 text-left"
                  data-testid="nav-mobile-logout"
                >
                  <LogOut className="h-4 w-4" strokeWidth={1.5} /> Sign out
                </button>
              </>
            ) : (
              <button
                onClick={() => { setOpen(false); login(); }}
                className="flex items-center gap-2 text-[#0B302F] py-1 text-left"
                data-testid="nav-mobile-signin"
              >
                <LogIn className="h-4 w-4" strokeWidth={1.5} /> Sign in with Google
              </button>
            )}
            <Button
              onClick={() => { setOpen(false); onBookNow(); }}
              data-testid="nav-mobile-book-now"
              className="rounded-full bg-[#0B302F] text-[#FDFBF7] hover:bg-[#2A5C5A] mt-1"
            >
              Book a Session
            </Button>
          </div>
        </div>
      )}
    </header>
  );
}
